import { useEffect } from "react";

/**
 * Check if the active element is an input, textarea, select, or editable content
 */
function isEditableElement(element) {
  if (!element) return false;
  const tagName = element.tagName ? element.tagName.toLowerCase() : "";
  const isContentEditable = element.isContentEditable;
  const isInput = tagName === "input" || tagName === "textarea" || tagName === "select";
  return isInput || isContentEditable;
}

/**
 * useKeyboardShortcuts Hook
 * Global listener for fast navigation shortcuts:
 * - L: Leads
 * - D: Converted Deals
 * - P: Sales Pipeline
 * - A: Activities & Calendar
 * - R: Periodic Reports (Admin)
 * - E: Employee Management (Admin)
 * - S: Organization Settings (Admin)
 * - Esc: Blur inputs and close active overlays
 */
export function useKeyboardShortcuts({ role, onNavigateTab }) {
  useEffect(() => {
    if (!onNavigateTab || typeof window === "undefined") return;

    const handleKeyDown = (e) => {
      // Ignore if user is holding modifier keys (except Esc or standard keys)
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      // Handle Escape globally to blur active inputs
      if (e.key === "Escape") {
        if (isEditableElement(document.activeElement)) {
          document.activeElement.blur();
        }
        return;
      }

      // If user is typing in a form input, do not trigger single-key shortcuts
      if (isEditableElement(e.target) || isEditableElement(document.activeElement)) {
        return;
      }

      const key = e.key ? e.key.toLowerCase() : "";

      switch (key) {
        case "l":
          e.preventDefault();
          onNavigateTab("leads");
          break;
        case "d":
          e.preventDefault();
          onNavigateTab("converted");
          break;
        case "p":
          e.preventDefault();
          onNavigateTab("pipeline");
          break;
        case "a":
          e.preventDefault();
          onNavigateTab("activities");
          break;
        case "r":
          if (role === "admin") {
            e.preventDefault();
            onNavigateTab("reports");
          }
          break;
        case "e":
          if (role === "admin") {
            e.preventDefault();
            onNavigateTab("employees");
          }
          break;
        case "s":
          if (role === "admin") {
            e.preventDefault();
            onNavigateTab("settings");
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [role, onNavigateTab]);
}

export default useKeyboardShortcuts;
